import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { logger } from "../lib/logger";
import { PageHeader, Card, Button, Input, EmptyState } from "../components/ui-kit";
import { Download, Upload, Truck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";

const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  dispatched: "bg-[#1E3F20]/10 text-[#1E3F20]",
  "in-transit": "bg-blue-100 text-blue-800",
  delivered: "bg-emerald-100 text-emerald-800",
  overdue: "bg-red-100 text-red-800",
};

export default function DispatchesPage() {
  const { user } = useAuth();
  const canManage = ["admin", "manager", "director"].includes(user?.role);
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("pending");
  const [q, setQ] = useState("");
  const [uploading, setUploading] = useState(false);

  const load = async () => setOrders((await api.get("/orders")).data || []);
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pendingList = orders.filter((o) => ["pending", "approved"].includes(o.status));
  const dispatchedList = orders.filter((o) => ["dispatched", "in-transit", "delivered"].includes(o.status));

  const rows = tab === "pending" ? pendingList : dispatchedList;
  const filtered = useMemo(
    () => rows.filter((r) => (q ? [r.order_number, r.customer_name, r.user_name].some((v) => (v || "").toLowerCase().includes(q.toLowerCase())) : true)),
    [rows, q]
  );

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/dispatches/pending-orders.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "pending_orders_template.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const onUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: result } = await api.post("/dispatches/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${result.updated} order${result.updated === 1 ? "" : "s"} updated`);
      if (result.errors?.length) {
        toast.warning(`${result.errors.length} row(s) skipped — see console`);
        logger.warn("Dispatch upload errors:", result.errors);
      }
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Dispatches"
        subtitle="Update vehicle, driver, e-way & invoice against pending orders"
        actions={
          canManage && (
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" onClick={downloadTemplate} data-testid="dispatch-download">
                <Download className="w-4 h-4" /> Download pending
              </Button>
              <label className={`inline-flex items-center gap-2 cursor-pointer bg-[#1E3F20] text-white hover:bg-[#153217] rounded-lg px-4 py-2 font-semibold text-sm ${uploading ? "opacity-60 pointer-events-none" : ""}`}>
                <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Upload dispatches"}
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  data-testid="dispatch-upload"
                  onChange={(e) => onUpload(e.target.files?.[0])}
                />
              </label>
            </div>
          )
        }
      />

      <div className="mb-4 flex flex-wrap gap-2">
        <button
          onClick={() => setTab("pending")}
          data-testid="tab-pending"
          className={`px-4 py-2 rounded-lg text-sm font-semibold border ${tab === "pending" ? "bg-[#1E3F20] text-white border-[#1E3F20]" : "bg-white text-[#1E3F20] border-[#E5E2DC]"}`}
        >
          Pending ({pendingList.length})
        </button>
        <button
          onClick={() => setTab("dispatched")}
          data-testid="tab-dispatched"
          className={`px-4 py-2 rounded-lg text-sm font-semibold border ${tab === "dispatched" ? "bg-[#1E3F20] text-white border-[#1E3F20]" : "bg-white text-[#1E3F20] border-[#E5E2DC]"}`}
        >
          Dispatched ({dispatchedList.length})
        </button>
        <div className="ml-auto min-w-[240px]">
          <Input placeholder="Search order/customer…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="dispatch-search" />
        </div>
      </div>

      <Card className="p-0 overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-8">
            <EmptyState icon={Truck} title="Nothing to show" description={tab === "pending" ? "No pending orders." : "No dispatched orders yet."} />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-[#F9F8F6] border-b border-[#E5E2DC]">
                <tr className="text-left text-[11px] uppercase tracking-widest text-[#5C6B5E]">
                  <th className="px-4 py-3">Order</th>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3">Sales</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Vehicle</th>
                  <th className="px-4 py-3">Driver</th>
                  <th className="px-4 py-3">E-way / Invoice</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((o) => (
                  <tr key={o.id} className="border-b border-[#E5E2DC]" data-testid={`dispatch-row-${o.id}`}>
                    <td className="px-4 py-3 font-mono text-xs font-bold">{o.order_number || o.id.slice(0, 8)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{(o.date || "").slice(0, 10)}</td>
                    <td className="px-4 py-3 font-semibold">{o.customer_name}</td>
                    <td className="px-4 py-3 text-[#5C6B5E]">{o.user_name}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded ${STATUS_STYLE[o.status] || STATUS_STYLE.pending}`}>
                        {o.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{o.vehicle_number || "—"}</td>
                    <td className="px-4 py-3 text-xs">
                      {o.driver_name || "—"}
                      {o.driver_mobile && <div className="font-mono text-[#5C6B5E]">{o.driver_mobile}</div>}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {o.eway_bill_number && <div>E-way: <span className="font-mono">{o.eway_bill_number}</span></div>}
                      {o.invoice_number && <div>Inv: <span className="font-mono">{o.invoice_number}</span></div>}
                      {!o.eway_bill_number && !o.invoice_number && "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
